// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { bool, string, oneOfType, object, func, number } from 'prop-types';
import {
  ModalBody,
  ComposedModal,
  Button,
  TextInputSkeleton,
  SkeletonPlaceholder,
  Loading
} from '@carbon/react';
import { Carousel, Thumbs } from 'react-responsive-carousel';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import uniqueId from 'lodash/uniqueId';
import { useContextSelector } from 'use-context-selector';
import { Package, ChevronDown, Copy, X, Megaphone, SquareStack } from 'lucide-react';

import { UserContext } from 'Contexts/UserContext';
import Img from 'Containers/Image/Image';
import ProductPropType from 'Types/product';
import Badges from 'Components/Badges/Badges';
import { renderStock } from 'Components/Products/helpers';
import Price from 'Components/Price/Price';
import ProductPriceRequest from 'Containers/Products/ProductPriceRequest';
import AddToCart from 'Components/AddToCart/AddToCart';
import DeliveryEstimate from 'Components/Product/DeliveryEstimate';
import QuantityBasedPrice from 'Components/QuantityBasedPrice/QuantityBasedPrice';
import useNumberUtils from 'Hooks/useNumberUtils';
import useToastNotifications from 'Hooks/useToastNotifications';
import copyToClipboard from 'Utils/copyToClipboard';

const StyledCloseButton = styled(Button)`
  position: relative;
  top: -1rem;
  right: -1rem;
  padding: 0;
  margin: 0;
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #041B54 !important;
  border: 0;
  outline: 0;
  color: ${ props => props.theme.colors.white };
  z-index: 99;

  svg {
    transform: scale(1.25);
    fill: ${ props => props.theme.colors.white };
    margin: 0;
  }
`;

const StyledModalBody = styled(ModalBody)`
  display: flex;
  margin: 0 !important;
  padding: 0 !important;
  background-color: ${ props => props.theme.colors.shadowGray };
  max-height: 75vh;
  height: auto;
  overflow: hidden;

  .preview {
    position: relative;
    padding: 1rem;
    min-width: 47.5%;
    max-width: 47.5%;
    width: 100%;
    overflow: auto;

    .badges {
      position: absolute;
      top: 1.15rem;
      left: 1.15rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
      z-index: 1;

      .specialPriceValidTo {
        margin: 0;
        font-size: 0.625rem;

        span:not(.badge) {
          display: none;
        }
      }

      > .badge {
        background-color: ${ props => props.theme.colors.white };
      }

      .badge {
        &.isEol,
        &.isClearance,
        &.specialPrice {
          color: ${ props => props.theme.products.colors.isEol };
        }

        width: 100%;
        font-size: 0.625rem;
        font-weight: 600;
        margin: 2px 5px;
        padding: 2px 5px;
        border-radius: 0;
      }
    }

    .carousel-root {
      width: 100%;
    }

    .slider .slide svg {
      max-width: 50%;
    }

    .carousel-slider {
      background-color: ${ props => props.theme.colors.white };
      padding: 2.25rem 1rem;
      height: 100%;
      text-align: center;

      .slider-wrapper {
        cursor: pointer;
      }

      li.slide .cds--skeleton__placeholder {
        margin: 0 auto;
      }
    }

    .carousel + .carousel {
      margin: 1.5rem -0.5rem;
    }

    img {
      position: relative;
      top: 50%;
      transform: translateY(-50%);
      user-select: none;
      height: auto;
      width: auto;
      max-height: 100%;
      max-width: 100%;
    }

    img.main {
      max-height: 330px;
      margin: 0 auto;
    }
  }

  .stock {
    &.inStock {
      span {
        color: ${ props => props.theme.products.colors.inStock };
      }
    }

    &.outOfStock {
      span {
        color: ${ props => props.theme.colors.default };
      }
    }

    &.onDemand {
      span {
        color: ${ props => props.theme.colors.darkGray };
      }
    }

    font-size: 0.875em;
  }

  .details {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 1rem;
    flex-grow: 1;
    background-color: ${ props => props.theme.colors.white };
    color: ${ props => props.theme.colors.darkGray };
    font-size: 1rem;
    overflow: auto;

    .titleContainer {
      display: flex;
      justify-content: space-between;

      .title {
        font-size: 1.25rem;
        line-height: 1.6rem;
        text-transform: uppercase;
        font-weight: 600;
        margin-bottom: 5px;
        text-align: left;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;

        &.showFullTitle {
          -webkit-line-clamp: unset;
        }
      }
    }

    .description {
      p > a {
        margin-left: 5px;
      }

      p {
        width: 100%;
        padding-right: 0;
        line-height: 1.5rem;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      p, a {
        font-size: 0.875rem;
      }

      a {
        color: ${ props => props.theme.colors.blue };
      }

      margin-bottom: 10px;
    }

    .minQuantity {
      margin: 5px 0;
      line-height: 1rem;
      text-align: right;
      font-size: 0.925rem;
      font-weight: normal;
      color: ${ props => props.theme.colors.darkGray };
      padding: 0;

      svg {
        position: relative;
        top: 3px;
        margin-right: 7px;
        transform: scale(0.9);
      }
    }

    .priceContainer {
      display: grid;
      grid-template-columns: calc(60% - 15px) 40%;
      grid-gap: 15px;
      margin: 5px 0;
      padding: 8px 0;
      border-top: 1px solid ${ props => props.theme.products.colors.border };
      border-bottom: 1px solid ${ props => props.theme.products.colors.border };

      > div {
        width: 100%;
      }

      .price {
        margin-top: 10px;
      }

      .oldPrice {
        color: ${ props => props.theme.colors.gray };
        font-size: 1.25em;
        line-height: 1.2em;
      }

      .currentPrice {
        font-size: 1.5em;
        line-height: 1.25em;
        color: ${ props => props.theme.colors.default };
      }

      .code,
      .stock {
        padding: 0;
        font-weight: 600;
        font-size: 0.875rem;

        span {
          font-weight: normal;
          margin: 0 3px;
        }

        svg {
          &.copy {
            cursor: pointer;
            top: 2px;
          }

          position: relative;
          margin-left: 2px;
        }
      }
    }

    .addToCartContainer {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin: 10px 0;

      p {
        padding-right: 0;
      }

      .quantitySelector {
        margin-right: 10px;
      }

      button {
        font-size: 1rem;
        height: 40px;
        text-align: center;
        min-height: 0;

        svg {
          margin-right: 5px;
        }

        &.stockAlert {
          border: 1px solid ${ props => props.theme.colors.default };
          color: ${ props => props.theme.colors.default };
          width: 150px;

          .cds--loading {
            position: static;
            margin-right: 5px;
          }
        }
      }
    }

    .attributes {
      > .title {
        margin-bottom: 10px;
        font-size: 1rem;
        font-weight: normal;
        text-transform: none;
        color: ${ props => props.theme.colors.darkGray };
      }
    }
  }
`;

const StyledAttributesContainer = styled.div`
  margin: 10px 0 25px;
  background-color: ${ props => props.theme.colors.white };

  > div {
    &:first-of-type {
      margin-top: 0;
    }
    &:last-of-type {
      margin-bottom: 0;
    }
    margin: 10px 0 15px;
  }

  .row {
    margin: 10px 0;
  }

  .title {
    margin-bottom: 5px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
  }

  .attributeData {
    border: 1px solid ${ props => props.theme.products.colors.addToFavoriteBorder };

    .row {
      margin: 0;
    }

    .row {
      display: flex;
      align-items: center;
      padding: 5px 12px;
      background-color: ${ props => props.theme.colors.white };

      &.odd {
        background-color: ${ props => props.theme.products.colors.promotionsRibbon };
      }

      p {
        &.name {
          width: 38%;
          color: ${ props => props.theme.colors.gray };
        }

        &.values {
          color: ${ props => props.theme.colors.darkGray };
          flex-grow: 1;
        }

        font-size: 0.875rem;
        text-align: left;
      }
    }
  }
`;

const StyledThumbs = styled.div`
  .thumbs-wrapper {
    margin: 15px 0 0;

    .control-arrow {
      top: 0;
      width: 24px;
      height: 100px;
      padding: 0;
      background-color: ${ props => props.theme.colors.shadowGray };
      border: 1px solid ${ props => props.theme.products.colors.border };
      margin: 0;
      opacity: 1;
    }

    .control-prev {
      &:before {
        position: relative;
        left: 3px;
        margin: 0;
        border: 0;
        content: ${ () => `url(${ChevronDown})` };
        transform: rotate(90deg) scale(2);
        width: 24px;
      }
    }

    .control-next {
      &:before {
        position: relative;
        right: 4px;
        margin: 0;
        border: 0;
        content: ${ () => `url(${ChevronDown})` };
        transform: rotate(-90deg) scale(2);
        width: 24px;
      }
    }
  }

  //.thumbs {
  //  margin: 0 -5px;
  //}

  .thumb {
    &.selected,
    &:hover {
      border: 1px solid ${ props => props.theme.products.colors.addToFavoriteText };
    }
    width: 100px;
    height: 100px;
    padding: 10px;
    border: 1px solid ${ props => props.theme.products.colors.border };
    //margin: 0 5px;
    background-color: ${ props => props.theme.colors.white };
    cursor: pointer;
    text-align: center;

    .cds--skeleton__placeholder {
      width: 100%;
      height: 100%;
    }

    img {
      position: relative;
      top: 50%;
      transform: translateY(-50%);
      height: auto;
      width: auto;
      max-height: 100%;
      max-width: 100%;
      margin: 0 auto;
      display: block;
    }
  }
`;

const StyledComposedModal = styled(ComposedModal)`
  .cds--modal-container {
    max-width: 1200px;
    border: none;
  }
`;

const ProductQuickViewModalComponent = ({
  open,
  error,
  onChangeQuantity,
  onAddToCart,
  addToCartLoading,
  onStockAlertModalOpen,
  onClose,
  product,
  selectedSlide,
  onSelectedSlide
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const userData = useContextSelector(UserContext, ({ data }) => data);
  const currency = userData?.defaultCurrency;
  const withVat = userData?.withVat;
  const accountManagerPhone = userData?.accountManager?.phone;
  const { formatNumber } = useNumberUtils();
  const toast = useToastNotifications();

  const copyCodeToClipboard = async code => {
    const resp = await copyToClipboard(code);

    if (resp) {
      toast.addNotification({
        type: 'success',
        title: t('products.copyCodeSuccess')
      });
    }
  };

  const memoizedCarouselItems = useMemo(
    () => {
      if (!product?.detailsLoaded) {
        return <SkeletonPlaceholder key={ uniqueId('productMainImagePreviewSkeleton') } />;
      }

      if (product?.pictures?.length) {
        return product.pictures.map(picture => (
          <Img
            key={ uniqueId(picture.urlOverlayPicture) }
            className="main"
            src={ picture.urlOverlayPicture }
            loading={ <Loading active withOverlay={ false } small /> }
            unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
            alt={ product.title }
          />
        ));
      }

      return <Package />;
    },
    [product.pictures, product.detailsLoaded]
  );

  const memoizedThumbnails = useMemo(
    () => {
      if (!product?.detailsLoaded) {
        return Array.from({ length: 5 }, () => (
          <SkeletonPlaceholder key={ uniqueId('productDetailsThumbnailSkeleton') } />
        ));
      }

      return (product?.pictures || []).map(picture => (
          <Img
            key={ uniqueId(picture.urlThumbnailPicture) }
            src={ picture.urlThumbnailPicture }
            loader={ <Loading small active withOverlay={ false } /> }
            unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
          />
      ));
    },
    [product.detailsLoaded, product.pictures]
  );

  let addToCartComponent;

  if (
    (product.quantity === null || product.quantity === undefined)
    && product.promoItem
  ) {
    addToCartComponent = <TextInputSkeleton />;
  } else if (product?.hasPriceRequest) {
    addToCartComponent = <ProductPriceRequest productId={ product.id } />;
  } else if ((
    product.stockValue === 0
    || product.stockValue < product.minQuantity
  ) && !Object.keys(product?.warnings || {}).length) {
    addToCartComponent = (
      <Button
        className="red stockAlert"
        kind="danger--ghost"
        onClick={ onStockAlertModalOpen }
        data-test="onStockAlert"
        data-testid={ product.id }
      >
        <Megaphone />
        { t('products.stockAlert') }
      </Button>
    );
  } else {
    addToCartComponent = (
      <AddToCart
        className="addToCart"
        disabled={
          addToCartLoading
          || product.warnings?.blocked_prices
          || product.warnings?.blocked_stocks
          || product.warnings?.blocked_orders
        }
        showSelector={ !(product.warnings?.blocked_prices || product.warnings?.blocked_stocks || product.warnings?.blocked_orders) }
        loading={ addToCartLoading }
        onClick={ onAddToCart }
        onChange={ onChangeQuantity }
        trackingContext={ {
          location: 'product_quick_view',
          product_sku: product?.code,
        } }
        value={ product.quantity || 1 }
        min={ product.minQuantity || 1 }
        step={ product.minQuantity || 1 }
        max={ product.stockValue }
        data-test="quickViewChangeQty"
        data-testid={ product.id }
      />
    );
  }

  const internalModalContent = useMemo(() => {
    if (!product?.id) {
      return null;
    }

    return (
      <>
        <div className="preview">
          { !product?.hasPriceRequest && !product.warnings?.blocked_prices && (
            <Badges
              isEol={ product.isEol }
              isOnDemandOnly={
                // eslint-disable-next-line no-nested-ternary
                product.reservedStockValue > 0
                  ? false
                  : product.stockValue <= 0
                    ? product.isOnDemandOnly
                    : false
              }
              isResealed={ !!product.originalCode }
              savings={ Math.round(((product.price - product.promoPrice) / product.price) * 100) / 100 }
              isNew={ product.isNew }
              isSupplierOffer={ !product?.hasPrice }
              hasSupplierOffers={ product.hasSupplierOffers }
            />
          ) }
          <Carousel
            showArrows={ false }
            emulateTouch
            showStatus={ false }
            showIndicators={ false }
            dynamicHeight={ false }
            showThumbs={ false }
            onClickItem={ onSelectedSlide }
            selectedItem={ selectedSlide }
          >
            { memoizedCarouselItems }
          </Carousel>
          { product?.pictures?.length !== 1 && (
            <StyledThumbs theme={ theme }>
              <Thumbs
                thumbWidth={ 100 }
                selectedItem={ selectedSlide }
                onSelectItem={ onSelectedSlide }
                emulateTouch
                showArrows
              >
                { memoizedThumbnails }
              </Thumbs>
            </StyledThumbs>
          ) }
        </div>
        <div className="details">
          <div className="titleContainer">
            <h3 className={ `title ${ product?.editedTitle ? 'showFullTitle' : '' }` }>
              { product.title }
            </h3>
            <StyledCloseButton theme={ theme } onClick={ onClose } kind="primary">
              <X />
            </StyledCloseButton>
          </div>
          <div className="priceContainer">
            { product.id && product.code
              ? (
                <div>
                  <p className="code">
                    { t('products.code') }:
                    <span>{ product.code }</span>
                    <span><Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(product?.code || '') } /></span>
                  </p>
                  { !product.warnings?.blocked_stocks && (
                    renderStock({ t, product, info: { accountManagerPhone } })
                  ) }
                  { (!product?.hasPrice || product?.hasSupplierOffers) && (
                    <DeliveryEstimate
                      estimate={ product?.supplierOfferDeliveryTimeDays }
                      isExternalSupplier={ !product?.hasPrice }
                    />
                  ) }
                </div>
              )
              : <TextInputSkeleton />
            }
            { 'price' in product
              ? (!product?.hasPriceRequest && !product.warnings?.blocked_prices) && (
                <Price
                  price={ product.price }
                  promoPrice={ product.promoPrice }
                  withTooltip
                  withVat={ withVat }
                  displayVatInfo
                  tooltipContent={ `
                      ${ t('products.greenStamp') }
                      ${ formatNumber(product.greenStampValue, { currency }) }
                  ` }
                />
              )
              : <TextInputSkeleton />
            }
          </div>
          <div>
            { product.minQuantity > 1 && (
              <p className="minQuantity">
                <SquareStack />
                { t('products.multipleOf', { multiple: product.minQuantity }) }
              </p>
            ) }
          </div>
          <div className="addToCartContainer">
            { (
              !!product?.priceScales?.length
              && !(product?.warnings?.blocked_orders || product?.warnings?.blocked_prices)
            ) && <QuantityBasedPrice priceScales={ product.priceScales } /> }
            { addToCartComponent }
          </div>
          <div className="description">
            { product?.description?.length > 0 && (
              <p>
                { product.description }
              </p>
            ) }
            <Link
              className="seeMore"
              to={ `/products/${ product.slug }/pd/${ product.id }` }
              data-test="quickItemNavigation"
              data-testid={ product.id }
              state={ {
                component: 'product_quick_view',
                source: 'quick_view_modal',
                clickedOn: 'see_more',
                productId: product.id
              } }
            >
              { t('product.viewMoreQuickView') } &gt;
            </Link>
          </div>
          { error && (
            <p className="error">{ error }</p>
          ) }
          { (
            !error && (
              !product.detailsLoaded
              || (product.detailsLoaded && (product.groupAttributes || []).length)
            )
          )
            ? (
              <div className="attributes">
                <p className="title">{ t('product.specifications') }</p>
                <StyledAttributesContainer theme={ theme }>
                  { product.id && !product.detailsLoaded
                    ? Array.from({ length: 4 }, () => (
                      <TextInputSkeleton
                        key={ uniqueId('quickModalTextInputSkeleton') }
                        className="row"
                      />
                    ))
                    : (product?.groupAttributes || []).map(attributeGroup => (
                      <div key={ attributeGroup.group_id }>
                        <p className="title">
                          { attributeGroup.group_name || t('products.miscAttributeGroup') }
                        </p>
                        <div className="attributeData">
                          { attributeGroup.properties.map((prop, idx) => (
                            <div
                              key={ prop.name_id }
                              className={ `row ${ idx % 2 ? 'even' : 'odd' }` }
                            >
                              <p className="name">{ prop.name }</p>
                              <p className="values">{ prop.values.map(v => v.value).join(', ') }</p>
                            </div>
                          )) }
                        </div>
                      </div>
                    ))
                  }
                </StyledAttributesContainer>
              </div>
            )
            : null
          }
        </div>
      </>
    );
  }, [product, selectedSlide, addToCartLoading]);

  return (
    <StyledComposedModal
      open={ open }
      onClose={ onClose }
      className="QuickViewModal"
      size="lg"
      selectorPrimaryFocus=".cds--modal-content"
    >
      <StyledModalBody theme={ theme }>
        { internalModalContent }
      </StyledModalBody>
    </StyledComposedModal>
  );
};

ProductQuickViewModalComponent.propTypes = {
  open: bool,
  addToCartLoading: bool,
  error: string,
  onClose: func,
  onStockAlertModalOpen: func,
  onAddToCart: func,
  onChangeQuantity: func,
  product: oneOfType([ProductPropType, object]),
  selectedSlide: number,
  onSelectedSlide: func
};

export default ProductQuickViewModalComponent;
